import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Close, CameraAlt } from '@mui/icons-material';

// The scanner must sit above every stacked app modal (allocateZ lives in the
// low thousands), so we park it near the top of the z-index range.
const SCANNER_Z = 2000000;

// All OpenCV work happens in scanner-worker.js (off the main thread). OpenCV.js
// is self-hosted under /vendor so the worker loads it same-origin.
const OPENCV_URL = `${window.location.origin}${process.env.PUBLIC_URL || ''}/vendor/opencv.js`;
const WORKER_URL = `${process.env.PUBLIC_URL || ''}/scanner-worker.js`;

const DET_W = 384;                  // downscaled width sent to the worker for detection
const DETECT_INTERVAL_MS = 110;
const MIN_AREA_RATIO = 0.18;        // document must fill ≥18% of the frame to auto-capture
const MOVE_THRESH = 5;              // px (in detection space) = "steady"
const STABLE_HITS = 6;              // ~0.7s steady before auto-capture
const COOLDOWN_MS = 1300;
const JPEG_QUALITY = 0.9;

// Outline stability tuning. The raw per-frame contour is noisy, so we low-pass
// the corners and gate out junk detections instead of drawing every frame raw.
const SMOOTH_ALPHA = 0.3;           // 0 = frozen, 1 = no smoothing (follows raw instantly)
const DRAW_MIN_AREA_RATIO = 0.08;   // ignore tiny contours (noise)
const DRAW_MAX_AREA_RATIO = 0.97;   // ignore full-frame contours (false positive)
const SNAP_RATIO = 0.16;            // jump > this × frame diagonal ⇒ snap (new document) instead of glide
const MISS_TOLERANCE = 4;           // keep showing the last outline for N missed frames before hiding

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const allCorners = (c) =>
  c && c.topLeftCorner && c.topRightCorner && c.bottomLeftCorner && c.bottomRightCorner;
const quadArea = (c) => {
  const p = [c.topLeftCorner, c.topRightCorner, c.bottomRightCorner, c.bottomLeftCorner];
  let s = 0;
  for (let i = 0; i < 4; i += 1) {
    const j = (i + 1) % 4;
    s += p[i].x * p[j].y - p[j].x * p[i].y;
  }
  return Math.abs(s) / 2;
};
const maxMove = (a, b) => Math.max(
  dist(a.topLeftCorner, b.topLeftCorner),
  dist(a.topRightCorner, b.topRightCorner),
  dist(a.bottomLeftCorner, b.bottomLeftCorner),
  dist(a.bottomRightCorner, b.bottomRightCorner),
);
const scaleCorners = (c, f) => ({
  topLeftCorner: { x: c.topLeftCorner.x * f, y: c.topLeftCorner.y * f },
  topRightCorner: { x: c.topRightCorner.x * f, y: c.topRightCorner.y * f },
  bottomLeftCorner: { x: c.bottomLeftCorner.x * f, y: c.bottomLeftCorner.y * f },
  bottomRightCorner: { x: c.bottomRightCorner.x * f, y: c.bottomRightCorner.y * f },
});
const lerpPt = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const lerpCorners = (a, b, t) => ({
  topLeftCorner: lerpPt(a.topLeftCorner, b.topLeftCorner, t),
  topRightCorner: lerpPt(a.topRightCorner, b.topRightCorner, t),
  bottomLeftCorner: lerpPt(a.bottomLeftCorner, b.bottomLeftCorner, t),
  bottomRightCorner: lerpPt(a.bottomRightCorner, b.bottomRightCorner, t),
});

const STATUS_TEXT = {
  searching: 'Point at a document…',
  detected: 'Document found — hold steady',
  hold: 'Hold steady…',
  captured: 'Captured ✓',
};

// Full-screen document scanner: live edge outline, auto-capture on steadiness
// (toggleable), perspective-corrected/cropped output. Calls onCapture(file) with
// a JPEG File per page; stays open for continuous scanning until closed.
export default function DocumentScanner({ open, onClose, onCapture }) {
  const videoRef = React.useRef(null);
  const overlayRef = React.useRef(null);
  const detCanvasRef = React.useRef(null);
  const frameCanvasRef = React.useRef(null);
  const outCanvasRef = React.useRef(null);
  const workerRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const rafRef = React.useRef(0);

  const workerReadyRef = React.useRef(false);
  const detBusyRef = React.useRef(false);
  const capturingRef = React.useRef(false);
  const lastDetectRef = React.useRef(0);
  const detSizeRef = React.useRef({ detW: 0, detH: 0 });
  const cornersRef = React.useRef(null);     // { corners, detW, detH } — smoothed, used for draw + capture
  const smoothedRef = React.useRef(null);    // running low-passed corners
  const missRef = React.useRef(0);           // consecutive frames with no valid detection
  const prevCornersRef = React.useRef(null);
  const stableRef = React.useRef(0);
  const cooldownUntilRef = React.useRef(0);
  const countRef = React.useRef(0);
  const statusRef = React.useRef('searching');
  const autoModeRef = React.useRef(true);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [autoMode, setAutoMode] = React.useState(true);
  const [status, setStatus] = React.useState('searching');
  const [count, setCount] = React.useState(0);
  const [thumbs, setThumbs] = React.useState([]);
  const [flash, setFlash] = React.useState(false);

  // Keep the latest onCapture without re-running the camera effect.
  const onCaptureRef = React.useRef(onCapture);
  React.useEffect(() => { onCaptureRef.current = onCapture; }, [onCapture]);
  React.useEffect(() => { autoModeRef.current = autoMode; }, [autoMode]);

  const updateStatus = (s) => {
    if (statusRef.current !== s) { statusRef.current = s; setStatus(s); }
  };

  const drawOverlay = React.useCallback(() => {
    const overlay = overlayRef.current;
    const video = videoRef.current;
    if (!overlay || !video) return;
    const cw = video.clientWidth;
    const ch = video.clientHeight;
    if (!cw || !ch) return;
    if (overlay.width !== cw) overlay.width = cw;
    if (overlay.height !== ch) overlay.height = ch;
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, cw, ch);
    const cur = cornersRef.current;
    if (!cur) return;
    const sx = cw / cur.detW;
    const sy = ch / cur.detH;
    const C = cur.corners;
    const steady = statusRef.current === 'hold' || statusRef.current === 'captured';
    ctx.lineWidth = 4;
    ctx.strokeStyle = steady ? '#4caf50' : '#ff9800';
    ctx.fillStyle = steady ? 'rgba(76,175,80,0.18)' : 'rgba(255,152,0,0.12)';
    ctx.beginPath();
    ctx.moveTo(C.topLeftCorner.x * sx, C.topLeftCorner.y * sy);
    ctx.lineTo(C.topRightCorner.x * sx, C.topRightCorner.y * sy);
    ctx.lineTo(C.bottomRightCorner.x * sx, C.bottomRightCorner.y * sy);
    ctx.lineTo(C.bottomLeftCorner.x * sx, C.bottomLeftCorner.y * sy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }, []);

  const finalizeCapture = React.useCallback((thumb) => {
    if (thumb) setThumbs((prev) => [thumb, ...prev].slice(0, 6));
    setCount((c) => c + 1);
    cooldownUntilRef.current = performance.now() + COOLDOWN_MS;
    stableRef.current = 0;
    prevCornersRef.current = null;
    updateStatus('captured');
    setFlash(true);
    setTimeout(() => setFlash(false), 160);
  }, []);

  const emitBlob = React.useCallback((canvas) => {
    const thumb = canvas.toDataURL('image/jpeg', 0.5);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `scan-${Date.now()}-${countRef.current++}.jpg`, { type: 'image/jpeg' });
      if (onCaptureRef.current) onCaptureRef.current(file);
    }, 'image/jpeg', JPEG_QUALITY);
    finalizeCapture(thumb);
  }, [finalizeCapture]);

  const capture = React.useCallback(() => {
    const video = videoRef.current;
    const worker = workerRef.current;
    if (!video || !video.videoWidth || capturingRef.current) return;
    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const frame = frameCanvasRef.current;
    frame.width = vW;
    frame.height = vH;
    frame.getContext('2d').drawImage(video, 0, 0, vW, vH);

    const cur = cornersRef.current;
    if (cur && allCorners(cur.corners) && worker) {
      const f = vW / cur.detW;
      const sc = scaleCorners(cur.corners, f);
      const outW = Math.round(Math.max(dist(sc.topLeftCorner, sc.topRightCorner), dist(sc.bottomLeftCorner, sc.bottomRightCorner)));
      const outH = Math.round(Math.max(dist(sc.topLeftCorner, sc.bottomLeftCorner), dist(sc.topRightCorner, sc.bottomRightCorner)));
      if (outW > 0 && outH > 0) {
        const id = frame.getContext('2d').getImageData(0, 0, vW, vH);
        capturingRef.current = true;
        cooldownUntilRef.current = performance.now() + COOLDOWN_MS; // block auto re-trigger while warping
        worker.postMessage(
          { type: 'extract', buffer: id.data.buffer, width: vW, height: vH, corners: sc, outW, outH },
          [id.data.buffer],
        );
        return;
      }
    }
    // No detected quad → keep the full frame uncropped.
    emitBlob(frame);
  }, [emitBlob]);

  const evaluateAutoCapture = React.useCallback(() => {
    const now = performance.now();
    if (now < cooldownUntilRef.current || capturingRef.current) return;
    const cur = cornersRef.current;
    if (!cur) { stableRef.current = 0; prevCornersRef.current = null; updateStatus('searching'); return; }
    if (quadArea(cur.corners) < MIN_AREA_RATIO * cur.detW * cur.detH) {
      stableRef.current = 0;
      prevCornersRef.current = null;
      updateStatus('searching');
      return;
    }
    if (prevCornersRef.current) {
      if (maxMove(cur.corners, prevCornersRef.current) < MOVE_THRESH) stableRef.current += 1;
      else stableRef.current = 0;
    }
    prevCornersRef.current = cur.corners;
    updateStatus(stableRef.current >= 2 ? 'hold' : 'detected');
    if (autoModeRef.current && stableRef.current >= STABLE_HITS) capture();
  }, [capture]);

  const sendDetect = React.useCallback(() => {
    const video = videoRef.current;
    const worker = workerRef.current;
    const det = detCanvasRef.current;
    if (!video || !video.videoWidth || !worker) return;
    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const detW = DET_W;
    const detH = Math.round(DET_W * (vH / vW));
    det.width = detW;
    det.height = detH;
    const ctx = det.getContext('2d');
    ctx.drawImage(video, 0, 0, detW, detH);
    const id = ctx.getImageData(0, 0, detW, detH);
    detSizeRef.current = { detW, detH };
    detBusyRef.current = true;
    worker.postMessage({ type: 'detect', buffer: id.data.buffer, width: detW, height: detH }, [id.data.buffer]);
  }, []);

  // Start / stop the worker + camera + render loop with the dialog.
  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setThumbs([]);
    setCount(0);
    countRef.current = 0;
    cornersRef.current = null;
    smoothedRef.current = null;
    missRef.current = 0;
    prevCornersRef.current = null;
    stableRef.current = 0;
    detBusyRef.current = false;
    capturingRef.current = false;
    workerReadyRef.current = false;
    statusRef.current = 'searching';
    setStatus('searching');

    const tick = (ts) => {
      rafRef.current = requestAnimationFrame(tick);
      drawOverlay();
      const video = videoRef.current;
      if (!workerReadyRef.current || !video || video.readyState < 2) return;
      if (!detBusyRef.current && ts - lastDetectRef.current >= DETECT_INTERVAL_MS) {
        lastDetectRef.current = ts;
        sendDetect();
      }
    };

    let worker;
    try {
      worker = new Worker(WORKER_URL);
    } catch (e) {
      setError('Could not start the scanner worker.');
      setLoading(false);
      return undefined;
    }
    workerRef.current = worker;

    worker.onmessage = (e) => {
      const msg = e.data;
      if (msg.type === 'ready') {
        workerReadyRef.current = true;
        // Camera may already be running; flip out of the loading state.
        if (!cancelled && streamRef.current) setLoading(false);
      } else if (msg.type === 'error') {
        if (!cancelled) { setError(msg.message || 'Scanner failed to load.'); setLoading(false); }
      } else if (msg.type === 'corners') {
        detBusyRef.current = false;
        const { detW, detH } = detSizeRef.current;
        const raw = msg.corners;
        const frameArea = detW * detH;
        const area = raw && allCorners(raw) ? quadArea(raw) : 0;
        const valid = area >= DRAW_MIN_AREA_RATIO * frameArea && area <= DRAW_MAX_AREA_RATIO * frameArea;
        if (valid) {
          const prev = smoothedRef.current;
          if (!prev) {
            smoothedRef.current = raw;
          } else {
            const diag = Math.hypot(detW, detH);
            smoothedRef.current = maxMove(raw, prev) > SNAP_RATIO * diag
              ? raw                                  // big jump ⇒ new document, follow immediately
              : lerpCorners(prev, raw, SMOOTH_ALPHA); // small change ⇒ glide
          }
          missRef.current = 0;
          cornersRef.current = { corners: smoothedRef.current, detW, detH };
        } else {
          missRef.current += 1;
          if (missRef.current > MISS_TOLERANCE) { smoothedRef.current = null; cornersRef.current = null; }
        }
        evaluateAutoCapture();
      } else if (msg.type === 'extracted') {
        capturingRef.current = false;
        if (msg.data) {
          const out = outCanvasRef.current;
          out.width = msg.width;
          out.height = msg.height;
          out.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(msg.data), msg.width, msg.height), 0, 0);
          emitBlob(out);
        } else {
          emitBlob(frameCanvasRef.current); // fallback to the full frame
        }
      }
    };
    worker.onerror = () => {
      if (!cancelled) { setError('Scanner worker error.'); setLoading(false); }
    };
    worker.postMessage({ type: 'init', opencvUrl: OPENCV_URL });

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.setAttribute('playsinline', 'true');
          await video.play().catch(() => {});
        }
        if (workerReadyRef.current && !cancelled) setLoading(false);
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e && e.name === 'NotAllowedError'
            ? 'Camera permission denied. Enable camera access and try again.'
            : 'Could not start the camera.');
          setLoading(false);
        }
      }
    })();

    const videoEl = videoRef.current;
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null; }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoEl) videoEl.srcObject = null;
    };
  }, [open, drawOverlay, sendDetect, evaluateAutoCapture, emitBlob]);

  if (!open) return null;

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: SCANNER_Z, bgcolor: '#000', display: 'flex', flexDirection: 'column' }}>
      {/* Hidden work canvases */}
      <canvas ref={detCanvasRef} style={{ display: 'none' }} />
      <canvas ref={frameCanvasRef} style={{ display: 'none' }} />
      <canvas ref={outCanvasRef} style={{ display: 'none' }} />

      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, color: '#fff', gap: 1 }}>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Scan documents</Typography>
        <FormControlLabel
          sx={{ color: '#fff', mr: 0 }}
          control={<Switch checked={autoMode} onChange={(e) => setAutoMode(e.target.checked)} />}
          label="Auto"
        />
      </Box>

      {/* Camera area */}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
          <video ref={videoRef} muted playsInline style={{ width: '100%', maxHeight: '100%', display: 'block', objectFit: 'contain' }} />
          <canvas ref={overlayRef} style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', height: '100%', pointerEvents: 'none' }} />
        </Box>

        {flash && <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#fff', opacity: 0.7 }} />}

        {(loading || error) && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', gap: 2, p: 3, textAlign: 'center' }}>
            {error ? (
              <>
                <Typography>{error}</Typography>
                <Button variant="outlined" color="inherit" onClick={onClose}>Close</Button>
              </>
            ) : (
              <>
                <CircularProgress color="inherit" />
                <Typography variant="body2">Loading scanner…</Typography>
              </>
            )}
          </Box>
        )}

        {!loading && !error && (
          <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', px: 2, py: 0.5, borderRadius: 5 }}>
            <Typography variant="caption">{STATUS_TEXT[status] || ''}</Typography>
          </Box>
        )}
      </Box>

      {/* Bottom bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 2, color: '#fff', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5, flex: 1, overflowX: 'auto' }}>
          {thumbs.map((src, i) => (
            <Box key={i} component="img" src={src} alt="" sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, border: '1px solid rgba(255,255,255,0.4)' }} />
          ))}
        </Box>

        <IconButton
          onClick={capture}
          disabled={loading || !!error}
          sx={{ width: 68, height: 68, border: '4px solid #fff', color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
        >
          <CameraAlt />
        </IconButton>

        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Button variant="contained" onClick={onClose} disabled={loading && !error}>
            Done{count ? ` (${count})` : ''}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
